import {
  CreditPlanFailureReason,
  CreditPlanResponseModel,
  CreditPlanStatus,
  GetReportingPeriodDefaultFormModel,
  ReportingPeriodType,
  UserTrackerData,
  UserTrackerDataRequest
} from '@/@types/client-api';
import { ICreditPlanBlockProps } from '@/components/blocks/CreditPlan/CreditPlanBlock';
import ExecutingCreditPlanSidePanel from '@/components/ui/CreditPlan/Panels/ExecutingCreditPlanSidePanel';
import GeneratedCreditPlanSidePanel from '@/components/ui/CreditPlan/Panels/GeneratedCreditPlanSidePanel';
import { useGetLicenseData } from '@/components/ui/LicensesPage/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CreateCreditPlanOptions, creditPlanContext } from './context';
import { useGetCreditPlan, useGetCreditPlans } from './hooks';
import MultiCreditPlanSidePanel from '@/components/ui/CreditPlan/Panels/MultiCreditPlanSidePanel';
import { useAuth } from 'react-oidc-context';
import useCreditTracker from '@/components/ui/CreditTracker/hooks/useCreditTrackerHook';
import {
  CREDIT_PLANS_SPECIAL_STATUS_COOKIE_NAME,
  CreditPlanJurisdiction,
  CreditPlansSparseDataFlowSteps,
  CreditPlansSpecialStatusCookie
} from '@/components/ui/CreditPlan/util/constants';
import {
  buildCreditPlansCookieName,
  getCreditPlanCardRegionImageSources,
  getJurisdictionDisclaimer,
  getJurisdictionFromTrackerLicense,
  getSelectedSpecialStatusFlags,
  hasTransitionalFlagOption,
  hasTransitionalOrNewlyAdmittedFlag,
  shouldSkipPracticeAreaSteps
} from '@/components/ui/CreditPlan/util/CreditPlanHelpers';
import {
  getComplianceModalFormFields,
  getCreditRegionSearchUrl,
  getSpecialStatuses,
  periodIsMissingComplianceInformation,
  reportingPeriodTypeByName
} from '@/components/ui/CreditTracker/util/TrackerHelpers';
import {
  selectCreditPlansDebugModeEnabled,
  selectCreditPlansEnabled,
  selectCreditPlanLoadingStepIndex,
  selectCreditPlansGlobalContent
} from '@/redux/selectors/creditPlanSelectors';
import { useAppDispatch, useAppSelector } from '@/hooks';
import CreditPlanFailureSidePanel from '@/components/ui/CreditPlan/Panels/CreditPlanFailureSidePanel';
import {
  CreateCreditPlanRequest,
  useCreateCreditPlanMutation,
  useGetExcludedLicensesQuery,
  useLazyGetCreditPlanQuery
} from '@/redux/api/client/creditPlan';
import { type ComplianceModalCopy } from '@/components/ui/CreditTracker/Modals/ComplianceModal';
import { ComplianceFormFields } from '@/components/ui/CreditTracker/Forms/ComplianceForm';
import { datadogRum, DataDogSeverityLevel } from '@/lib/DatadogService';
import { incrementPlanLoadingStepIndex, upsertPlan } from '@/redux/slices/creditPlanSlice';
import { ClientAPI } from '@/redux/api';
import { ComplianceInformationModalBlock } from '@/@types/content';
import { isEqual } from 'lodash';
import {
  ComplianceModalStep,
  SpecialStatusSelector
} from '@/components/ui/CreditTracker/util/constants';
import CreditPlansSparseDataFlow from '@/components/ui/CreditPlan/CreditPlansSparseDataFlow';
import {
  useGetUserPracticeAreasQuery,
  useLazyGetUserPracticeAreasQuery
} from '@/redux/api/client/practiceAreas';
import { useLazyGetReportingPeriodDefaultFormQuery } from '@/redux/api/client/creditTracker';
import { creditTrackerUpdateData } from '@/redux/slices/creditTrackerSlice';
import { getCookieValues, setCookieValues } from '@/utils/cookie-list-values';
import { useAnalytics } from '@/analytics/hooks/useAnalytics';
import { AnalyticsContext, AnalyticsEventName } from '@/analytics/constants';

export type CreditPlanPanelType = 'multi' | 'single';

type PendingCreditPlanGeneration = {
  jurisdiction: CreditPlanJurisdiction;
  options?: CreateCreditPlanOptions;
};

type ForcedSpecialStatus = {
  flags: string[];
  statuses: SpecialStatusSelector[];
};

const defaultCreditPlanComplianceModalCopy: Partial<ComplianceModalCopy> = {
  explanationStep: {
    icon: 'upcoming',
    heading: 'Add your compliance period for a more accurate Credit Plan',
    messagingContentArea:
      'A Credit Plan can be built without a tailored compliance period, however, we recommend adding one for the best results.',
    primaryButtonText: 'Add compliance period',
    secondaryButtonText: 'Generate without compliance period'
  },
  experienceSwitchStep: {
    heading: 'We need more information to build your Credit Plan',
    experienceSwitcherLabel: 'Are you newly-admitted or experienced? *',
    requiredFieldHelpText: '* Indicates required field',
    primaryButtonText: 'Continue'
  },
  formStep: {
    heading: 'Add compliance information',
    requiredFieldHelpText: '* Indicates required field',
    primaryButtonText: 'Generate my plan'
  }
};

const CreditPlanProvider = ({ children }: { children: React.ReactNode }) => {
  const [
    { open, type, jurisdiction, overrideContent, previousType } = { open: false },
    setOpenPlan
  ] = useState<{
    open: boolean;
    type: CreditPlanPanelType;
    previousType?: CreditPlanPanelType;
    jurisdiction?: string;
    overrideContent?: ICreditPlanBlockProps;
  }>();
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const indivSk = (auth.user?.profile?.IndividualSK ?? auth.user?.profile?.individualSk) as string;

  // #region selectors
  const globalBlockContent = useAppSelector(selectCreditPlansGlobalContent);
  const creditPlansEnabled = useAppSelector(selectCreditPlansEnabled);
  const debugModeEnabled = useAppSelector(selectCreditPlansDebugModeEnabled);
  // #endregion

  // #region component state
  const [_, setProviderVersion] = useState<number>(1);
  const [isReloadingPlan, setIsReloadingPlan] = useState<boolean>(false);

  // These need to be separate due to property restrictions of our Modal component setOpen
  const [incompleteTrackerLicense, setIncompleteTrackerLicense] = useState<UserTrackerData>();
  const [incompleteTrackerLicenseModalOpen, setIncompleteTrackerLicenseModalOpen] =
    useState<boolean>(false);

  const [pendingCreditPlanGeneration, setPendingCreditPlanGeneration] =
    useState<PendingCreditPlanGeneration>();

  const [practiceAreaFlowOpen, setPracticeAreaFlowOpen] = useState(false);
  const [pendingPracticeAreaGeneration, setPendingPracticeAreaGeneration] =
    useState<PendingCreditPlanGeneration>();

  const [creditPlanDefaultForm, setCreditPlanDefaultForm] =
    useState<GetReportingPeriodDefaultFormModel>();
  const [creditPlanComplianceFormFields, setCreditPlanComplianceFormFields] =
    useState<ComplianceFormFields>();
  // #endregion

  // #region refs
  const isCheckingPracticeAreasRef = useRef(false);
  const defaultFormRequestIdRef = useRef(0);
  const pendingForcedComplianceSetupKeyRef = useRef<string>();
  const pendingCreditPlanGenerationRef = useRef<PendingCreditPlanGeneration>();
  //#endregion

  const { trackEvent } = useAnalytics();
  const specialStatusSelectionCookieName = useMemo(
    () => buildCreditPlansCookieName(CREDIT_PLANS_SPECIAL_STATUS_COOKIE_NAME, indivSk),
    [indivSk]
  );

  const getStoredCreditPlanSpecialStatuses = useCallback((): CreditPlansSpecialStatusCookie => {
    try {
      const storedVal = JSON.parse(
        getCookieValues(specialStatusSelectionCookieName).join(',')
      ) as unknown;
      return storedVal && typeof storedVal === 'object' && !Array.isArray(storedVal)
        ? (storedVal as CreditPlansSpecialStatusCookie)
        : {};
    } catch {
      return {};
    }
  }, [specialStatusSelectionCookieName]);

  const content = useMemo(
    () => overrideContent ?? globalBlockContent,
    [globalBlockContent, overrideContent]
  );

  const [allPlans, { isLoading: isCreditPlansLoading }] = useGetCreditPlans();
  const { data: excludedLicenses, isLoading: isLoadingExcludedLicenses } =
    useGetExcludedLicensesQuery(undefined, {
      skip: !auth.isAuthenticated || !creditPlansEnabled
    });

  const { data: userPracticeAreas } = useGetUserPracticeAreasQuery(undefined, {
    skip: !auth.isAuthenticated || !creditPlansEnabled || !!shouldSkipPracticeAreaSteps(indivSk)
  });

  const [openPlan] = useGetCreditPlan(jurisdiction);
  const { creditJurisdictions, licenses, isLoading: isLicensesLoading } = useGetLicenseData();
  const [createPlan] = useCreateCreditPlanMutation();
  const [getPlan] = useLazyGetCreditPlanQuery();
  const [getUserPracticeAreas] = useLazyGetUserPracticeAreasQuery();
  const [getReportingPeriodDefaultForm] = useLazyGetReportingPeriodDefaultFormQuery();

  const openPlanLoadingStepIndex = useAppSelector(state =>
    selectCreditPlanLoadingStepIndex(state, {
      processId: openPlan?.processId ?? undefined,
      licenseIdentifier: openPlan?.licenseIdentifier ?? undefined,
      jurisdiction: openPlan?.jurisdiction ?? jurisdiction ?? undefined
    })
  );

  const user = useMemo(
    () => parseInt(auth.user?.profile?.IndividualSK as string),
    [auth.user?.profile?.IndividualSK]
  );
  const creditPlanTrackerRequest = useMemo<UserTrackerDataRequest>(
    () => ({ individualSk: user }),
    [user]
  );
  // Credit plans do not own the Credit Tracker page loading state; modal-level loading covers this flow.
  const setComplianceModalLoading = useCallback(() => {}, []);

  const practiceAreaPromptStepOrder = useMemo(
    () => [
      CreditPlansSparseDataFlowSteps.addPracticeAreas,
      CreditPlansSparseDataFlowSteps.addSpecificPracticeAreas
    ],
    []
  );
  const compliancePromptStepOrder = useMemo(() => {
    return [CreditPlansSparseDataFlowSteps.addComplianceInformation];
  }, []);

  const hasPlans = useMemo(() => (allPlans?.length ?? 0) > 0, [allPlans?.length]);
  const creditTrackerArgs = useMemo(
    () => ({
      request: { individualSk: user },
      skipQuery: !auth.isAuthenticated || !hasPlans || !creditPlansEnabled
    }),
    [auth.isAuthenticated, creditPlansEnabled, hasPlans, user]
  );
  const { trackerLoading, trackerFetching, trackerData } = useCreditTracker(creditTrackerArgs);

  const trackerLicenses = useMemo(() => {
    if (!trackerData) return {};
    return Object.fromEntries(
      trackerData
        .filter(r => r.creditRegionShortDescription)
        ?.map(r => [r.creditRegionShortDescription!.toLowerCase(), r])
    );
  }, [trackerData]);

  const licensedCreditRegionSkSet = useMemo(() => {
    return new Set(licenses?.items?.map(license => license.creditRegion_SK) ?? []);
  }, [licenses?.items]);

  const excludedLicenseSet = useMemo(() => {
    return new Set(excludedLicenses ?? []);
  }, [excludedLicenses]);

  const normalizedExcludedLicenseSet = useMemo(
    () => new Set([...excludedLicenseSet].map(license => license.toLowerCase())),
    [excludedLicenseSet]
  );

  const invalidLicenseSet = useMemo(() => {
    return new Set(
      [...excludedLicenseSet]
        .filter(license => {
          const normalizedLicense = license.toLowerCase();
          const hasTransitionalOption = hasTransitionalFlagOption(
            trackerLicenses[normalizedLicense]?.jurisdiction?.cleStateTransitionOptions ?? []
          );

          return (
            !hasTransitionalOption || normalizedExcludedLicenseSet.has(`${normalizedLicense}-tr`)
          );
        })
        .map(license => license.toLowerCase())
    );
  }, [excludedLicenseSet, normalizedExcludedLicenseSet, trackerLicenses]);

  const allPlansByJurisdiction = useMemo(() => {
    const plansByJurisdiction = new Map<string, CreditPlanResponseModel>();
    for (const plan of allPlans ?? []) {
      const jurisdiction = plan.jurisdiction?.toLowerCase();
      if (!jurisdiction || plansByJurisdiction.has(jurisdiction)) continue;
      plansByJurisdiction.set(jurisdiction, plan);
    }

    return plansByJurisdiction;
  }, [allPlans]);

  const isLoading = useMemo(() => {
    return (
      !!isLicensesLoading ||
      !!trackerLoading ||
      !!trackerFetching ||
      !!isCreditPlansLoading ||
      !!isLoadingExcludedLicenses
    );
  }, [
    isCreditPlansLoading,
    isLicensesLoading,
    isLoadingExcludedLicenses,
    trackerFetching,
    trackerLoading
  ]);

  const jurisdictions = useMemo(() => {
    // Only include valid MCLE.
    const jurisdictions =
      creditJurisdictions?.filter(
        j => j.creditClass_SK == 1 && licensedCreditRegionSkSet.has(j.creditRegion_SK)
      ) ?? [];

    const result: CreditPlanJurisdiction[] = [];
    for (const j of jurisdictions) {
      const id = j?.id || '';
      const trackerLicense = trackerLicenses[id?.toLowerCase() ?? ''];

      const hasTransitionalOption = hasTransitionalFlagOption(
        trackerLicense?.jurisdiction?.cleStateTransitionOptions ?? []
      );

      const isTransitionalOrNewlyAdmitted =
        trackerLicense?.jurisdiction?.isTransitionalOrNewlyAdmitted || false;

      const keys = hasTransitionalOption || isTransitionalOrNewlyAdmitted ? [`${id}-TR`, id] : [id];
      if (keys.every(key => invalidLicenseSet.has(key.toLowerCase()))) continue;

      const portraitImage = getCreditPlanCardRegionImageSources(
        id,
        isTransitionalOrNewlyAdmitted,
        'portrait'
      );
      const landscapeImage = getCreditPlanCardRegionImageSources(
        id,
        isTransitionalOrNewlyAdmitted,
        'landscape'
      );

      const jurisdictionNamePrefix = j?.creditClass ? `${j.creditClass} for ` : '';
      let flags = trackerLicense?.jurisdiction?.activeFlags ?? [];
      const complianceGroup = trackerLicense?.complianceGroup_SK
        ? `#CLE-ComplianceGroupSk-${trackerLicense?.complianceGroup_SK}`
        : undefined;

      if (complianceGroup) {
        flags = [...flags, complianceGroup];
      }

      const plan = allPlansByJurisdiction.get(j.id?.toLowerCase() ?? '');
      result.push({
        ...j,
        name: j.name,
        plan: plan?.creditPlan,
        sortOrder: trackerLicense?.baseSortOrder ?? 0,
        shortDescription: `${jurisdictionNamePrefix}${j.stateName}`,
        longDescription: `${jurisdictionNamePrefix}${plan?.jurisdictionDisplayName ?? trackerLicense?.jurisdiction?.displayName ?? trackerLicense?.creditRegionLongDescription}`,
        compliancePeriodEndDate: trackerLicense?.jurisdiction?.clePeriod?.endDate ?? '',
        flags: flags,
        status: plan?.status,
        isTransitionalOrNewlyAdmitted,
        imagePortrait: portraitImage.src,
        imagePortraitSrcSet: portraitImage.srcSet,
        imageLandscape: landscapeImage.src,
        imageLandscapeSrcSet: landscapeImage.srcSet
      } as CreditPlanJurisdiction);
    }

    return result?.sort((a, b) => a.sortOrder! - b.sortOrder!);
  }, [
    creditJurisdictions,
    licensedCreditRegionSkSet,
    trackerLicenses,
    allPlansByJurisdiction,
    invalidLicenseSet
  ]);

  const jurisdictionOrder = useMemo(() => {
    return new Map(
      jurisdictions?.map((jurisdiction, index) => [jurisdiction.id ?? '', index]) ?? []
    );
  }, [jurisdictions]);

  const openJurisdiction = useMemo(() => {
    if (!jurisdiction) return null;

    const found = jurisdictions?.find(j => j.id === jurisdiction);

    if (found) {
      return {
        ...found,
        isTransitionalOrNewlyAdmitted:
          found.flags?.some(a => (openPlan?.licenseIdentifier?.indexOf(a) ?? 0) > 0) || false
      };
    }
    return null;
  }, [jurisdiction, jurisdictions, openPlan?.licenseIdentifier]);

  const openJurisdictionId = openJurisdiction?.id ?? openPlan?.jurisdiction ?? jurisdiction;
  const openJurisdictionName =
    !!openJurisdiction?.stateName && !!openJurisdiction?.creditClass
      ? `${openJurisdiction?.stateName} ${openJurisdiction?.creditClass}`
      : (openJurisdiction?.longDescription ?? openJurisdictionId);

  const openJurisdictionBrowseUrl = useMemo(
    () =>
      openJurisdictionId
        ? getCreditRegionSearchUrl(
            openJurisdictionId,
            !!openJurisdiction?.isTransitionalOrNewlyAdmitted
          )
        : undefined,
    [openJurisdictionId, openJurisdiction?.isTransitionalOrNewlyAdmitted]
  );

  const creditPlanComplianceModalCopy: Partial<ComplianceModalCopy> = useMemo(() => {
    let result = {
      ...content,
      ...defaultCreditPlanComplianceModalCopy
    } as ComplianceModalCopy;

    const cmsContent = globalBlockContent
      ?.compliancePeriodModalContent?.[0] as ComplianceInformationModalBlock;
    if (cmsContent) {
      result = {
        ...result,
        explanationStep: {
          ...result.explanationStep,
          ...cmsContent.explanationStep,
          jurisdictionDisclaimer: getJurisdictionDisclaimer(
            cmsContent.explanationStep?.jurisdictionDisclaimers ?? [],
            jurisdiction
          )
        },
        experienceSwitchStep: {
          ...result.experienceSwitchStep,
          ...cmsContent.experienceSwitcherStep,
          jurisdictionDisclaimer: getJurisdictionDisclaimer(
            cmsContent.experienceSwitcherStep?.jurisdictionDisclaimers ?? [],
            jurisdiction
          )
        },
        formStep: {
          ...result.formStep,
          ...cmsContent.formStep,
          jurisdictionDisclaimer: getJurisdictionDisclaimer(
            cmsContent.formStep?.jurisdictionDisclaimers ?? [],
            jurisdiction
          )
        }
      };
    }
    return result;
  }, [content, globalBlockContent?.compliancePeriodModalContent, jurisdiction]);

  const showBackButton = useMemo(() => previousType == 'multi', [previousType]);

  /**
   * Deduplicate plans by jurisdiction (prefer completed over failed, latest wins within same status),
   * then sort to guarantee the same order as credit plan block cards.
   */
  const creditPlans = useMemo(() => {
    if (!allPlans) return allPlans;

    const byJurisdiction = new Map<string, CreditPlanResponseModel>();
    for (const plan of allPlans) {
      const key = plan.jurisdiction?.toLowerCase();
      if (!key) continue;
      const existing = byJurisdiction.get(key);
      const planIsCompleted = plan.status === CreditPlanStatus.COMPLETED;
      const existingIsCompleted = existing?.status === CreditPlanStatus.COMPLETED;
      // Prefer completed; among same category, take latest (later in array)
      if (!existing || planIsCompleted || !existingIsCompleted) {
        byJurisdiction.set(key, plan);
      }
    }

    return Array.from(byJurisdiction.values()).sort(
      (a, b) =>
        (jurisdictionOrder.get(a.jurisdiction ?? '') ?? -1) -
        (jurisdictionOrder.get(b.jurisdiction ?? '') ?? -1)
    );
  }, [allPlans, jurisdictionOrder]);

  const complianceModalFormFields = useMemo(() => {
    return creditPlanComplianceFormFields ?? getComplianceModalFormFields(incompleteTrackerLicense);
  }, [creditPlanComplianceFormFields, incompleteTrackerLicense]);

  const getCreditPlanSpecialStatuses = useCallback(
    (license?: UserTrackerData) => {
      const creditRegion = license?.creditRegionShortDescription;
      const storedVal = getStoredCreditPlanSpecialStatuses();
      if (creditRegion && storedVal[creditRegion]) return storedVal[creditRegion];

      const statusesByText = new Map<string, SpecialStatusSelector>();
      getSpecialStatuses(license?.jurisdiction?.cleStateTransitionOptions ?? [])?.forEach(status =>
        statusesByText.set(status.text, { ...status, value: 'False' })
      );
      return [...statusesByText.values()];
    },
    [getStoredCreditPlanSpecialStatuses]
  );

  const creditPlanComplianceGroups = useMemo(() => {
    if ((creditPlanDefaultForm?.complianceGroups?.length ?? 0) > 0)
      return creditPlanDefaultForm?.complianceGroups;

    return incompleteTrackerLicense?.complianceGroups;
  }, [creditPlanDefaultForm?.complianceGroups, incompleteTrackerLicense?.complianceGroups]);

  const getForcedSpecialStatus = useCallback(
    (license?: UserTrackerData): ForcedSpecialStatus | undefined => {
      const creditRegion = license?.creditRegionShortDescription?.toLowerCase();
      if (!creditRegion) return;

      const baseExcluded = normalizedExcludedLicenseSet.has(creditRegion);
      const transitionalExcluded = normalizedExcludedLicenseSet.has(`${creditRegion}-tr`);
      if (baseExcluded === transitionalExcluded) return;

      const statusesByText = new Map<string, SpecialStatusSelector>();
      getSpecialStatuses(license?.jurisdiction?.cleStateTransitionOptions ?? [])?.forEach(status =>
        statusesByText.set(status.text, status)
      );
      const availableStatuses = [...statusesByText.values()];
      const targetStatusValue = baseExcluded ? 'true' : 'false';
      const targetStatus = availableStatuses.find(status => {
        return status.value.toLowerCase() === targetStatusValue;
      });
      if (!targetStatus) return;

      const statuses = availableStatuses.map(status => ({
        ...status,
        value: `${status.text === targetStatus.text}`
      }));

      return {
        statuses,
        flags: baseExcluded ? getSelectedSpecialStatusFlags(statuses, license) : []
      };
    },
    [normalizedExcludedLicenseSet]
  );

  const forcedSpecialStatus = useMemo(
    () => getForcedSpecialStatus(incompleteTrackerLicense),
    [getForcedSpecialStatus, incompleteTrackerLicense]
  );

  const creditPlanSpecialStatuses = useMemo(
    () => forcedSpecialStatus?.statuses ?? getCreditPlanSpecialStatuses(incompleteTrackerLicense),
    [forcedSpecialStatus?.statuses, getCreditPlanSpecialStatuses, incompleteTrackerLicense]
  );

  const creditPlanComplianceModalStepSequence = useMemo(
    () =>
      forcedSpecialStatus
        ? [ComplianceModalStep.Explanation, ComplianceModalStep.Form]
        : [
            ComplianceModalStep.ExperienceSwitch,
            ComplianceModalStep.Explanation,
            ComplianceModalStep.Form
          ],
    [forcedSpecialStatus]
  );

  const prepareCreditPlanComplianceFormFields = useCallback(
    async (
      license: UserTrackerData,
      statuses: SpecialStatusSelector[],
      persist: boolean = true,
      selectedFlags?: string[]
    ): Promise<ComplianceFormFields | undefined> => {
      const creditRegion = license.creditRegionShortDescription;
      if (!creditRegion) return;

      const flags = selectedFlags ?? getSelectedSpecialStatusFlags(statuses, license);
      const requestId = ++defaultFormRequestIdRef.current;

      const hasTransitionalOrNewlyAdmitted = hasTransitionalOrNewlyAdmittedFlag(flags);

      try {
        const defaultForm = await getReportingPeriodDefaultForm(
          {
            creditRegion,
            effectiveDate: new Date().toISOString(),
            flags
          },
          true
        ).unwrap();

        if (requestId !== defaultFormRequestIdRef.current) return;

        const rawReportingPeriodType =
          defaultForm.reportingPeriodType ??
          (defaultForm as { ReportingPeriodType?: string | ReportingPeriodType })
            .ReportingPeriodType;

        const reportingPeriodType =
          typeof rawReportingPeriodType === 'string'
            ? reportingPeriodTypeByName[rawReportingPeriodType]
            : rawReportingPeriodType;

        if (!reportingPeriodType) {
          datadogRum.addError(
            new Error(`Unknown reportingPeriodType: ${String(rawReportingPeriodType)}`)
          );
          const fallbackComplianceFormFields = {
            ...getComplianceModalFormFields(license),
            jurisdiction: {
              ...license.jurisdiction,
              isTransitionalOrNewlyAdmitted: hasTransitionalOrNewlyAdmitted
            },
            ruleSetFlag: flags
          };
          setCreditPlanComplianceFormFields(fallbackComplianceFormFields);
          return fallbackComplianceFormFields;
        }

        setCreditPlanDefaultForm({ ...defaultForm, reportingPeriodType });
        dispatch(
          creditTrackerUpdateData({
            ...license,
            jurisdiction: {
              ...license.jurisdiction,
              activeFlags: flags,
              isTransitionalOrNewlyAdmitted: hasTransitionalOrNewlyAdmitted,
              jurisdictionReportingPeriod: {
                ...license.jurisdiction?.jurisdictionReportingPeriod,
                reportingPeriodType
              }
            }
          })
        );

        if (persist && license.creditRegionShortDescription) {
          const val = getStoredCreditPlanSpecialStatuses();
          val[license.creditRegionShortDescription] = statuses;
          setCookieValues(specialStatusSelectionCookieName, [JSON.stringify(val)], 1, 5);
        }

        const nextComplianceFormFields = {
          ...getComplianceModalFormFields(license),
          jurisdiction: {
            ...license.jurisdiction,
            isTransitionalOrNewlyAdmitted: hasTransitionalOrNewlyAdmitted
          },
          reportingPeriod: reportingPeriodType,
          ruleSetFlag: flags
        };
        setCreditPlanComplianceFormFields(nextComplianceFormFields);
        return nextComplianceFormFields;
      } catch (error) {
        if (requestId !== defaultFormRequestIdRef.current) return;

        datadogRum.addError(error);
        const fallbackComplianceFormFields = {
          ...getComplianceModalFormFields(license),
          jurisdiction: {
            ...license.jurisdiction,
            isTransitionalOrNewlyAdmitted: hasTransitionalOrNewlyAdmitted
          },
          ruleSetFlag: flags
        };
        setCreditPlanComplianceFormFields(fallbackComplianceFormFields);
        return fallbackComplianceFormFields;
      }
    },
    [
      dispatch,
      getReportingPeriodDefaultForm,
      getStoredCreditPlanSpecialStatuses,
      specialStatusSelectionCookieName
    ]
  );

  const handleCreditPlanSpecialStatusChange = useCallback(
    (statuses: SpecialStatusSelector[]): Promise<ComplianceFormFields | undefined> => {
      if (!incompleteTrackerLicense) return Promise.resolve(undefined);
      return prepareCreditPlanComplianceFormFields(incompleteTrackerLicense, statuses);
    },
    [incompleteTrackerLicense, prepareCreditPlanComplianceFormFields]
  );

  const startCreditPlanGeneration = useCallback(
    (jurisdiction: CreditPlanJurisdiction, options?: CreateCreditPlanOptions) => {
      if (!creditPlansEnabled || !jurisdiction.id) return;

      trackEvent(AnalyticsEventName.GenerateCreditPlan, {
        context: AnalyticsContext.Homepage,
        jurisdiction: jurisdiction.longDescription || 'unknown'
      });

      const planContent = options?.content ?? overrideContent;
      setOpenPlan({
        open: true,
        type: 'single',
        jurisdiction: jurisdiction.id,
        overrideContent: planContent,
        previousType
      });

      const request: CreateCreditPlanRequest = {
        indivSk: auth?.user?.profile?.sub,
        jurisdiction: jurisdiction.id,
        flags: jurisdiction.flags
      };

      if (jurisdiction.compliancePeriodEndDate) {
        request.complianceEndDate = jurisdiction.compliancePeriodEndDate;
      }

      createPlan(request)
        .unwrap()
        .then(_response => {
          console.info('Credit plan create started successfully, response:', _response);
          // Redux slice upserts initial credit plan response.
          // SignalR connection will upsert further updates.
        })
        .catch(ex => {
          datadogRum.addError(ex);
          dispatch(
            upsertPlan({
              jurisdiction: jurisdiction.id ?? '',
              plan: {
                jurisdiction: jurisdiction.id,
                status: CreditPlanStatus.FAILED,
                failureReason: CreditPlanFailureReason.UNABLE_TO_START_PLAN_GENERATION
              }
            })
          );
        });
    },
    [
      auth?.user?.profile?.sub,
      createPlan,
      creditPlansEnabled,
      dispatch,
      overrideContent,
      previousType,
      trackEvent
    ]
  );

  const generateAfterPracticeAreaGate = useCallback(
    async (jurisdiction: CreditPlanJurisdiction, options?: CreateCreditPlanOptions) => {
      if (!options?.promptForPracticeAreas || shouldSkipPracticeAreaSteps(indivSk)) {
        startCreditPlanGeneration(jurisdiction, options);
        return;
      }

      if (isCheckingPracticeAreasRef.current) {
        return;
      }

      isCheckingPracticeAreasRef.current = true;
      if (Array.isArray(userPracticeAreas?.items) && userPracticeAreas.items.length === 0) {
        setPendingPracticeAreaGeneration({ jurisdiction, options });
        setPracticeAreaFlowOpen(true);
        isCheckingPracticeAreasRef.current = false;
        return;
      }

      if (!Array.isArray(userPracticeAreas?.items)) {
        try {
          const practiceAreas = await getUserPracticeAreas(undefined, true).unwrap();
          if (practiceAreas?.items?.length === 0) {
            setPendingPracticeAreaGeneration({ jurisdiction, options });
            setPracticeAreaFlowOpen(true);
            return;
          }
        } catch {
          // Do not block plan generation if the optional practice-area gate cannot load.
        } finally {
          isCheckingPracticeAreasRef.current = false;
        }

        startCreditPlanGeneration(jurisdiction, options);
        return;
      }

      isCheckingPracticeAreasRef.current = false;

      startCreditPlanGeneration(jurisdiction, options);
    },
    [getUserPracticeAreas, indivSk, startCreditPlanGeneration, userPracticeAreas?.items]
  );

  const getTrackerLicenseFromSparseLicense = useCallback(
    (
      jurisdiction: CreditPlanJurisdiction,
      options?: CreateCreditPlanOptions
    ): UserTrackerData | undefined => {
      const sparseLicense = options?.sparseLicense;
      const creditRegion = jurisdiction.id;
      if (!sparseLicense || !creditRegion) return;

      return {
        customerId: user,
        creditRegion_SK: sparseLicense.creditRegion_SK ?? jurisdiction.creditRegion_SK,
        creditRegionName: sparseLicense.stateDescription ?? jurisdiction.name,
        creditRegionShortDescription: creditRegion,
        creditRegionLongDescription:
          sparseLicense.stateDescription ?? jurisdiction.longDescription ?? jurisdiction.name,
        complianceGroup_SK: sparseLicense.complianceGroup_SK,
        jurisdiction: {
          activeFlags: jurisdiction.flags ?? [],
          clePeriod: {
            isActivelySelected: true,
            isUserInfoIncomplete: true,
            startDate: '',
            endDate: ''
          },
          displayName:
            sparseLicense.stateDescription ?? jurisdiction.longDescription ?? jurisdiction.name,
          key: creditRegion,
          requireSetup: true
        },
        complianceGroups: sparseLicense.complianceGroups ?? []
      };
    },
    [user]
  );

  const handleComplianceModalComplete = useCallback(
    (incompleteTrackerLicense: UserTrackerData, complianceFormFields?: ComplianceFormFields) => {
      if (!creditPlansEnabled) return;

      const options = pendingCreditPlanGeneration?.options;
      const completedComplianceFormFields = complianceFormFields ?? creditPlanComplianceFormFields;
      const jurisdiction = {
        ...pendingCreditPlanGeneration?.jurisdiction,
        ...getJurisdictionFromTrackerLicense(
          incompleteTrackerLicense,
          completedComplianceFormFields
        ),
        id:
          incompleteTrackerLicense.creditRegionShortDescription ||
          pendingCreditPlanGeneration?.jurisdiction.id
      } as CreditPlanJurisdiction;

      const nextPendingGeneration = { jurisdiction, options };
      pendingCreditPlanGenerationRef.current = nextPendingGeneration;
      setPendingCreditPlanGeneration(nextPendingGeneration);
      setIncompleteTrackerLicense(incompleteTrackerLicense);
      dispatch(ClientAPI.util.invalidateTags(['CreditTrackerData']));
    },
    [
      creditPlansEnabled,
      creditPlanComplianceFormFields,
      dispatch,
      pendingCreditPlanGeneration?.jurisdiction,
      pendingCreditPlanGeneration?.options
    ]
  );

  const handleDefaultCreditPlanGeneration = useCallback(() => {
    if (!incompleteTrackerLicense) return;

    const license: UserTrackerData = {
      ...incompleteTrackerLicense,
      complianceGroup_SK:
        incompleteTrackerLicense.complianceGroup_SK ||
        incompleteTrackerLicense.complianceGroups?.[0]?.sk,
      jurisdiction: {
        ...incompleteTrackerLicense.jurisdiction,
        clePeriod: {
          ...incompleteTrackerLicense.jurisdiction?.clePeriod,
          endDate: incompleteTrackerLicense.jurisdiction?.clePeriod?.endDate || ''
        }
      }
    };

    return handleComplianceModalComplete(license);
  }, [handleComplianceModalComplete, incompleteTrackerLicense]);

  const validateTrackerComplianceSetupStatus = useCallback(
    async (jurisdiction: CreditPlanJurisdiction, options?: CreateCreditPlanOptions) => {
      const j = jurisdiction.id ?? '';
      const license =
        trackerLicenses[j?.toLowerCase()] ??
        getTrackerLicenseFromSparseLicense(jurisdiction, options);
      if (license?.jurisdiction) {
        const result = periodIsMissingComplianceInformation(
          license?.jurisdiction?.clePeriod,
          license?.jurisdiction?.requireSetup
        );

        const hasActivePlan =
          !options?.sparseLicense && creditPlans?.some(a => a.jurisdiction == j);
        const isOpen = result && !hasActivePlan;

        if (isOpen) {
          const forcedStatus = getForcedSpecialStatus(license);
          setIncompleteTrackerLicense(license);
          if (forcedStatus) {
            const setupKey = `${license.creditRegionShortDescription}:${forcedStatus.flags.join('|')}`;
            if (pendingForcedComplianceSetupKeyRef.current === setupKey) return false;

            pendingForcedComplianceSetupKeyRef.current = setupKey;
            try {
              await prepareCreditPlanComplianceFormFields(
                license,
                forcedStatus.statuses,
                false,
                forcedStatus.flags
              );
            } finally {
              pendingForcedComplianceSetupKeyRef.current = undefined;
            }
          }
          setIncompleteTrackerLicenseModalOpen(true);
          return false;
        }
      }
      return true;
    },
    [
      creditPlans,
      getForcedSpecialStatus,
      getTrackerLicenseFromSparseLicense,
      prepareCreditPlanComplianceFormFields,
      trackerLicenses
    ]
  );

  const createCreditPlan = useCallback(
    async (jurisdiction: CreditPlanJurisdiction, options?: CreateCreditPlanOptions) => {
      if (!creditPlansEnabled) return;

      if (!jurisdiction?.id) {
        datadogRum.addError(
          new Error('[createCreditPlan] Missing jurisdiction id while creating plan.'),
          {
            severityLevel: DataDogSeverityLevel.Warning
          }
        );
        return;
      }

      if (!(await validateTrackerComplianceSetupStatus(jurisdiction, options))) {
        pendingCreditPlanGenerationRef.current = { jurisdiction, options };
        setPendingCreditPlanGeneration({ jurisdiction, options });
        return;
      }

      generateAfterPracticeAreaGate(jurisdiction, options);
    },
    [creditPlansEnabled, generateAfterPracticeAreaGate, validateTrackerComplianceSetupStatus]
  );

  const openPanel = useCallback(
    async (
      jurisdiction?: string | null,
      overrideContent?: ICreditPlanBlockProps,
      previousType?: CreditPlanPanelType
    ) => {
      if (!creditPlansEnabled) return;

      const canOpen = await validateTrackerComplianceSetupStatus({
        id: jurisdiction
      } as CreditPlanJurisdiction);

      setOpenPlan({
        open: canOpen,
        type: jurisdiction || creditPlans?.length == 1 ? 'single' : 'multi',
        jurisdiction: jurisdiction ?? undefined,
        overrideContent,
        previousType: previousType
      });
    },
    [creditPlans?.length, creditPlansEnabled, validateTrackerComplianceSetupStatus]
  );

  function handleClose() {
    // keep the previous state for smoother closing animation (content doesn't get lost)
    setOpenPlan(prev => ({ ...prev!, open: false, previousType: undefined }));
  }

  function handlePanelBackButton() {
    // keep the previous state for smoother closing animation (content doesn't get lost)
    setOpenPlan(prev => ({
      open: true,
      type: 'multi',
      overrideContent: prev?.overrideContent,
      previousType: undefined
    }));
  }

  const getCreditPlan = useCallback(async () => {
    if (!creditPlansEnabled) return;

    if (openPlan?.processId) {
      setIsReloadingPlan(true);
      const { isError, isSuccess, data } = await getPlan(openPlan.processId);
      setIsReloadingPlan(false);

      if (isSuccess) {
        setOpenPlan(prev => ({
          open: true,
          type: 'single',
          jurisdiction: prev?.jurisdiction ?? data?.jurisdiction ?? '',
          overrideContent: prev?.overrideContent
        }));
      } else if (isError) {
        datadogRum.addError(new Error('[getCreditPlan] Failed to refetch single credit plan.'), {
          severityLevel: DataDogSeverityLevel.Error
        });
      }
      setProviderVersion(prev => prev + 1);
    }
  }, [creditPlansEnabled, getPlan, openPlan?.processId]);

  const advanceOpenPlanLoadingStep = useCallback(
    (stepCount: number) => {
      dispatch(
        incrementPlanLoadingStepIndex({
          processId: openPlan?.processId ?? undefined,
          licenseIdentifier: openPlan?.licenseIdentifier ?? undefined,
          jurisdiction: openPlan?.jurisdiction ?? jurisdiction ?? '',
          stepCount
        })
      );
    },
    [
      dispatch,
      openPlan?.licenseIdentifier,
      openPlan?.processId,
      openPlan?.jurisdiction,
      jurisdiction
    ]
  );

  const complianceJurisdiction = pendingCreditPlanGeneration?.jurisdiction.id ?? jurisdiction;

  useEffect(() => {
    if (!incompleteTrackerLicenseModalOpen) return;

    const current = trackerLicenses?.[complianceJurisdiction?.toLowerCase() ?? ''];
    if (!!current && !isEqual(incompleteTrackerLicense, current)) {
      setIncompleteTrackerLicense(current);
    }
  }, [
    complianceJurisdiction,
    incompleteTrackerLicense,
    incompleteTrackerLicenseModalOpen,
    trackerLicenses
  ]);

  useEffect(() => {
    if (!incompleteTrackerLicenseModalOpen) {
      setIncompleteTrackerLicense(undefined);
      setPendingCreditPlanGeneration(undefined);
      pendingCreditPlanGenerationRef.current = undefined;
      setCreditPlanDefaultForm(undefined);
      setCreditPlanComplianceFormFields(undefined);
      pendingForcedComplianceSetupKeyRef.current = undefined;
      defaultFormRequestIdRef.current += 1;
    }
  }, [incompleteTrackerLicenseModalOpen]);

  const handleComplianceFlowComplete = useCallback(() => {
    const pending = pendingCreditPlanGenerationRef.current ?? pendingCreditPlanGeneration;
    if (!pending) return;

    setIncompleteTrackerLicense(undefined);
    setIncompleteTrackerLicenseModalOpen(false);
    setPendingCreditPlanGeneration(undefined);
    pendingCreditPlanGenerationRef.current = undefined;
    generateAfterPracticeAreaGate(pending.jurisdiction, pending.options);
  }, [generateAfterPracticeAreaGate, pendingCreditPlanGeneration]);

  const handlePracticeAreaFlowComplete = useCallback(() => {
    const pending = pendingPracticeAreaGeneration;
    if (!pending) return;

    setPracticeAreaFlowOpen(false);
    setPendingPracticeAreaGeneration(undefined);
    startCreditPlanGeneration(pending.jurisdiction, pending.options);
  }, [pendingPracticeAreaGeneration, startCreditPlanGeneration]);

  useEffect(() => {
    if (!practiceAreaFlowOpen) {
      setPendingPracticeAreaGeneration(undefined);
    }
  }, [practiceAreaFlowOpen]);

  const practiceAreaPromptContent = useMemo(() => {
    const promptContent = pendingPracticeAreaGeneration?.options?.content ?? globalBlockContent;
    return {
      [CreditPlansSparseDataFlowSteps.addPracticeAreas]: promptContent?.practiceAreas,
      [CreditPlansSparseDataFlowSteps.addSpecificPracticeAreas]:
        promptContent?.specificPracticeAreas
    };
  }, [globalBlockContent, pendingPracticeAreaGeneration?.options?.content]);

  const compliancePromptContent = useMemo(() => {
    const promptContent = pendingCreditPlanGeneration?.options?.content ?? globalBlockContent;
    return {
      [CreditPlansSparseDataFlowSteps.addPracticeAreas]: promptContent?.practiceAreas,
      [CreditPlansSparseDataFlowSteps.addSpecificPracticeAreas]:
        promptContent?.specificPracticeAreas
    };
  }, [globalBlockContent, pendingCreditPlanGeneration?.options?.content]);

  return (
    <creditPlanContext.Provider
      value={{
        openPanel,
        handlePanelBackButton,
        createCreditPlan,
        jurisdictions,
        isLoading,
        creditPlans,
        isPanelOpen: open ?? false,
        closePanel: handleClose
      }}
    >
      {children}

      {/* View the credit plan */}
      <GeneratedCreditPlanSidePanel
        isOpen={
          open &&
          type === 'single' &&
          !!openPlan &&
          (openPlan.status == CreditPlanStatus.COMPLETED ||
            (openPlan.status == CreditPlanStatus.FAILED &&
              openPlan.failureReason == CreditPlanFailureReason.GENERAL))
        }
        onOpenChange={handleClose}
        jurisdictionId={openJurisdictionId ?? undefined}
        jurisdictionName={openJurisdictionName ?? undefined}
        complianceEndDate={openPlan?.complianceEndDate ?? undefined}
        flags={openJurisdiction?.flags ?? undefined}
        {...content}
        content={{
          ...content?.singlePlanSidePanelContent,
          alerts: openPlan?.alertMessages ?? []
        }}
        debugModeEnabled={debugModeEnabled}
        onBackButtonPress={showBackButton ? handlePanelBackButton : undefined}
        creditPlanLoadFailureContent={content?.creditPlanLoadFailure}
      />

      {/* View the execution process */}
      <ExecutingCreditPlanSidePanel
        isOpen={
          open &&
          type === 'single' &&
          (!openPlan ||
            [CreditPlanStatus.EXECUTING, CreditPlanStatus.STARTED].includes(openPlan.status!))
        }
        onOpenChange={handleClose}
        content={content?.executingSidePanelContent}
        onBackButtonPress={showBackButton ? handlePanelBackButton : undefined}
        loadingStepIndex={openPlanLoadingStepIndex}
        onAdvanceLoadingStepIndex={advanceOpenPlanLoadingStep}
      />

      {/* Select from multiple credit plans */}
      <MultiCreditPlanSidePanel
        isOpen={open && type === 'multi' && creditPlans?.length > 1}
        onOpenChange={handleClose}
        content={content?.multiPlanSidePanelContent}
        openPanel={openPanel}
        jurisdictions={jurisdictions}
        isLoading={isLoading}
        creditPlans={creditPlans}
      />

      {/* Show a failure panel */}
      <CreditPlanFailureSidePanel
        isOpen={open && type === 'single' && openPlan?.status == CreditPlanStatus.FAILED}
        onOpenChange={handleClose}
        jurisdictionName={openJurisdictionName ?? undefined}
        failureReason={openPlan?.failureReason}
        debugInformation={openPlan?.debugInformation}
        debugModeEnabled={debugModeEnabled}
        openJurisdictionBrowseUrl={openJurisdictionBrowseUrl}
        planId={openPlan?.processId ?? undefined}
        planItem={openPlan ?? undefined}
        handleBackButton={showBackButton ? handlePanelBackButton : undefined}
        creditPlanIncompleteFailureContent={content?.creditPlanIncompleteFailure}
        creditPlanLoadFailureContent={content?.creditPlanLoadFailure}
        isLoading={isReloadingPlan}
        createCreditPlan={() => {
          setProviderVersion(prev => prev + 1);
          const retryJurisdiction =
            openJurisdiction ??
            jurisdictions?.find(
              j => j.id === (openPlan?.jurisdiction ?? jurisdiction ?? '').toString()
            );

          if (!retryJurisdiction?.id) {
            datadogRum.addError(
              new Error(
                '[CreditPlanFailureSidePanel] Unable to resolve jurisdiction for create retry.'
              ),
              {
                severityLevel: DataDogSeverityLevel.Warning
              }
            );
            return;
          }

          createCreditPlan(retryJurisdiction);
        }}
        getCreditPlan={() => {
          datadogRum.addAction('Retry Get Credit Plan', {
            Jurisdiction: ''
          });
          getCreditPlan();
        }}
      />

      {/* Sparse Data Flow */}
      {!!incompleteTrackerLicenseModalOpen && (
        <CreditPlansSparseDataFlow
          key={`${incompleteTrackerLicense?.creditRegionShortDescription}-complianceFlow`}
          open={!!incompleteTrackerLicenseModalOpen}
          setOpen={setIncompleteTrackerLicenseModalOpen}
          stepOrder={compliancePromptStepOrder}
          saveLicenseOnSubmit={false}
          practiceAreaModalCooldown={
            pendingCreditPlanGeneration?.options?.practiceAreaModalCooldown
          }
          content={compliancePromptContent}
          complianceModalProps={{
            customerSk: user,
            isMissingCompliancePeriod: true,
            showStepNumbers: false,
            complianceGroups: creditPlanComplianceGroups,
            jurisdiction: incompleteTrackerLicense?.jurisdiction,
            trackerLicense: incompleteTrackerLicense,
            eyebrowText: incompleteTrackerLicense?.creditRegionLongDescription ?? '',
            selectedPeriod: 'current',
            complianceFormFields: complianceModalFormFields,
            specialStatuses: creditPlanSpecialStatuses,
            copy: creditPlanComplianceModalCopy,
            lastTrackerRequest: creditPlanTrackerRequest,
            setIsLoading: setComplianceModalLoading,
            reportingPeriodOverride: creditPlanDefaultForm?.reportingPeriodType,
            commitSpecialStatusOnFormSubmit: true,
            onSpecialStatusChange: handleCreditPlanSpecialStatusChange,
            isTransitionalOrNewlyAdmittedRegion: !!creditPlanSpecialStatuses?.some(a =>
              a.text?.toLowerCase().includes('experienced')
            ),
            useBackButtonNavigation: true,
            modalStepSequence: creditPlanComplianceModalStepSequence,
            onComplete: compliancePeriod =>
              incompleteTrackerLicense &&
              handleComplianceModalComplete(incompleteTrackerLicense, compliancePeriod),
            onSecondaryButtonPress: handleDefaultCreditPlanGeneration
          }}
          onComplete={handleComplianceFlowComplete}
        />
      )}

      {!!pendingPracticeAreaGeneration && (
        <CreditPlansSparseDataFlow
          open={practiceAreaFlowOpen}
          setOpen={setPracticeAreaFlowOpen}
          stepOrder={practiceAreaPromptStepOrder}
          saveLicenseOnSubmit={false}
          practiceAreaModalCooldown={
            pendingPracticeAreaGeneration.options?.practiceAreaModalCooldown
          }
          content={practiceAreaPromptContent}
          onComplete={handlePracticeAreaFlowComplete}
        />
      )}
    </creditPlanContext.Provider>
  );
};
export default CreditPlanProvider;
